import { Fragment, type ReactNode } from "react";

// Minimal markdown renderer for the concierge chat. Handles only what the AI
// actually emits: bold, italic, paragraphs, line breaks, bulleted lists,
// numbered lists. Intentionally tolerant of in-progress streaming output:
// unclosed **bold opens render as literal asterisks until the closing tokens
// arrive.

const BULLET_RE = /^[-*]\s+(.*)/;
const NUMBERED_RE = /^\d+\.\s+(.*)/;

function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  // Match **bold** (greedy across non-asterisk) or *italic*
  const pattern = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g;
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const m = match[0];
    if (m.startsWith("**")) {
      parts.push(<strong key={`b-${key++}`}>{m.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={`i-${key++}`}>{m.slice(1, -1)}</em>);
    }
    lastIndex = match.index + m.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : [text];
}

function renderBlock(block: string, key: number): ReactNode {
  const lines = block.split("\n").filter((l) => l.length > 0);

  if (lines.length > 0 && lines.every((l) => BULLET_RE.test(l))) {
    return (
      <ul key={`ul-${key}`} className="list-disc pl-5 my-2 space-y-1">
        {lines.map((l, i) => (
          <li key={i}>{renderInline(l.match(BULLET_RE)![1])}</li>
        ))}
      </ul>
    );
  }

  if (lines.length > 0 && lines.every((l) => NUMBERED_RE.test(l))) {
    return (
      <ol key={`ol-${key}`} className="list-decimal pl-5 my-2 space-y-1">
        {lines.map((l, i) => (
          <li key={i}>{renderInline(l.match(NUMBERED_RE)![1])}</li>
        ))}
      </ol>
    );
  }

  // Regular paragraph. Single newlines within a block become <br />.
  return (
    <p key={`p-${key}`} className="my-2 first:mt-0 last:mb-0">
      {lines.map((line, i) => (
        <Fragment key={i}>
          {i > 0 && <br />}
          {renderInline(line)}
        </Fragment>
      ))}
    </p>
  );
}

export function renderMarkdown(text: string): ReactNode {
  if (!text) return null;
  // Split on two-or-more newlines into paragraph-like blocks.
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, i) => renderBlock(block, i));
}
